import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { TEXT_SIZE_CLASS, type Scale } from "./tokens";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: Scale;
  wrapperClassName?: string;
}

/** Reemplazo de `<Textarea>` de Mantine. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { label, description, error, size = "sm", required, className, wrapperClassName, id, rows = 3, ...props },
    ref
  ) => {
    const autoId = id ?? props.name;
    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {label && (
          <label htmlFor={autoId} className="text-sm font-medium text-foreground">
            {label}
            {required && <span className="text-peligro-600"> *</span>}
          </label>
        )}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <textarea
          ref={ref}
          id={autoId}
          rows={rows}
          className={cn(
            "w-full rounded-md border bg-background px-3 py-2 transition-[border-color,box-shadow] ease-out-quint duration-140",
            "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "placeholder:text-muted-foreground",
            error ? "border-peligro-600" : "border-input",
            TEXT_SIZE_CLASS[size],
            className
          )}
          {...props}
        />
        {error && typeof error !== "boolean" && <p className="text-xs text-peligro-600">{error}</p>}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
